/**
 * features/settings/ExpenseCategoriesTab.tsx
 */

import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { ApiError } from '~/lib/api';
import type { ExpenseCategory } from '~/lib/types';
import { Button, Card, EmptyState, Field, Input, TableSkeleton } from '~/ui/primitives';
import { ConfirmDialog, Dialog, useToast } from '~/ui/overlays';
import {
  useCreateExpenseCategory,
  useDeleteExpenseCategory,
  useExpenseCategories,
  useUpdateExpenseCategory,
} from '~/features/expenses/hooks';

export function ExpenseCategoriesTab() {
  const { data: categories, isLoading } = useExpenseCategories();
  const create = useCreateExpenseCategory();
  const update = useUpdateExpenseCategory();
  const remove = useDeleteExpenseCategory();
  const toast = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseCategory | null>(null);
  const [name, setName] = useState('');
  const [deleting, setDeleting] = useState<ExpenseCategory | null>(null);

  function openCreate() {
    setEditing(null);
    setName('');
    setFormOpen(true);
  }

  function openEdit(category: ExpenseCategory) {
    setEditing(category);
    setName(category.name);
    setFormOpen(true);
  }

  async function handleSave() {
    if (!name.trim()) {
      toast.error('กรุณากรอกชื่อหมวดหมู่');
      return;
    }

    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, name: name.trim() });
        toast.success('บันทึกหมวดหมู่แล้ว');
      } else {
        await create.mutateAsync(name.trim());
        toast.success('เพิ่มหมวดหมู่แล้ว');
      }
      setFormOpen(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'บันทึกไม่สำเร็จ');
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success('ลบหมวดหมู่แล้ว');
      setDeleting(null);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'ลบไม่สำเร็จ');
    }
  }

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Button variant="primary" onClick={openCreate}>
          <Plus size={16} />
          เพิ่มหมวดหมู่
        </Button>
      </div>

      <Card>
        {isLoading ? (
          <TableSkeleton columns={2} />
        ) : !categories || categories.length === 0 ? (
          <EmptyState title="ยังไม่มีหมวดหมู่ค่าใช้จ่าย" />
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {categories.map((category) => (
              <div key={category.id} className="flex items-center justify-between px-4 py-2.5">
                <span className="text-sm text-fg">{category.name}</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" iconOnly aria-label="แก้ไข" onClick={() => openEdit(category)}>
                    <Pencil size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label="ลบ"
                    onClick={() => setDeleting(category)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? 'แก้ไขหมวดหมู่' : 'เพิ่มหมวดหมู่ใหม่'}
        size="sm"
        footer={
          <>
            <Button onClick={() => setFormOpen(false)}>ยกเลิก</Button>
            <Button
              variant="primary"
              onClick={() => void handleSave()}
              loading={create.isPending || update.isPending}
            >
              บันทึก
            </Button>
          </>
        }
      >
        <Field label="ชื่อหมวดหมู่" required>
          <Input value={name} onChange={(event) => setName(event.target.value)} autoFocus />
        </Field>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => void handleDelete()}
        title="ลบหมวดหมู่"
        message={`ต้องการลบหมวดหมู่ "${deleting?.name}" ใช่หรือไม่ ลบไม่ได้ถ้ามีค่าใช้จ่ายที่ใช้หมวดหมู่นี้อยู่`}
        confirmLabel="ลบ"
        loading={remove.isPending}
      />
    </div>
  );
}
